/**
 * src/lib/tokenStore.js
 *
 * IndexedDB persistence for JWT film roll tokens.
 * Replaces localStorage for token storage — more robust,
 * works well with Capacitor when we go native.
 *
 * Schema:
 *   DB: "blockamatic"
 *   Store: "tokens"
 *   Key: styleId (e.g. "kodachrome")
 *   Value: { styleId, token, shots, shotsRemaining, purchasedAt }
 */

import { openDB } from "idb";

const DB_NAME    = "blockamatic";
const DB_VERSION = 2;
const STORE_NAME = "tokens";
const PHOTOS_STORE_NAME = "photos";

async function getDB() {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "styleId" });
      }
      if (!db.objectStoreNames.contains(PHOTOS_STORE_NAME)) {
        db.createObjectStore(PHOTOS_STORE_NAME, { keyPath: "id" });
      }
    },
  });
}

// ── Save a newly purchased token ──────────────────────────────────────────────

export async function saveToken(styleId, token, shots) {
  const db = await getDB();
  await db.put(STORE_NAME, {
    styleId,
    token,
    shots,
    shotsRemaining: shots,
    purchasedAt: Date.now(),
  });
}

// ── Get token for a style ─────────────────────────────────────────────────────

export async function getToken(styleId) {
  const db = await getDB();
  return db.get(STORE_NAME, styleId);
}

// ── Get all tokens (for inventory display) ────────────────────────────────────

export async function getAllTokens() {
  const db = await getDB();
  const all = await db.getAll(STORE_NAME);
  // Returns map of styleId → { shotsRemaining, token, ... }
  return Object.fromEntries(all.map(t => [t.styleId, t]));
}

// ── Update token after a verified capture ─────────────────────────────────────
// Called after /api/verify returns a newToken with decremented shotsUsed

export async function updateToken(styleId, newToken, shotsRemaining) {
  const db = await getDB();
  const existing = await db.get(STORE_NAME, styleId);
  if (!existing) return;

  await db.put(STORE_NAME, {
    ...existing,
    token: newToken,
    shotsRemaining,
  });
}

// ── Delete a token (exhausted or expired) ────────────────────────────────────

export async function deleteToken(styleId) {
  const db = await getDB();
  await db.delete(STORE_NAME, styleId);
}

// ── Load inventory for UI (shots remaining per style) ─────────────────────────

export async function loadInventory() {
  const tokens = await getAllTokens();
  return {
    standard:   Infinity, // always free
    kodachrome: tokens.kodachrome?.shotsRemaining ?? 0,
    trix:       tokens.trix?.shotsRemaining       ?? 0,
    cinestill:  tokens.cinestill?.shotsRemaining  ?? 0,
    lomochrome: tokens.lomochrome?.shotsRemaining ?? 0,
  };
}
